import React, { useMemo, useState, useEffect } from "react";
import { observer } from "mobx-react";
import Board from "react-trello";
import { CSVLink } from "react-csv";

import * as commonS from "@styles/common";
import MainContentHeader from "@components/MainContentHeader";
import Loader from "@components/Loader";
import StudentHint from "@components/Hints/StudentHint";
import SchoolClassHint from "@components/Hints/SchoolClassHint";
import Checkbox from "@mui/material/Checkbox";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import useCurrentProject from "@hooks/useCurrentProject";
import studentsApi from "@api/students.api";

import friends from '@images/friends.png';

import stores from "@stores";

import * as S from "./styles";
import {
  getGender,
  getGermanLanguageLevelForStudent,
  getHelpWithStudy,
  getKnowledgeLevel,
  getSpecialNeeds,
} from "@helpers/getGermanNaming";

interface Props {
  distributionMap: any;
  isLoading: boolean;
  isManualMode: boolean;
  enableManualMode: () => void;
  fetchDistribution: () => void;
  setIsLoading: (arg: boolean) => void;
  getBooleanLabel: (obj: any, key: string) => string;
  generateIcon: (obj: any, classItem: any) => any;
}

const StudentsDistribution = ({
  distributionMap,
  isLoading,
  fetchDistribution,
  enableManualMode,
  setIsLoading,
  getBooleanLabel,
  isManualMode,
  generateIcon,
}: Props) => {
  const {
    projectsStore: { studentsFilter, setStudentsFilter },
  } = stores;
  const [anchorEl, setAnchorEl] = useState(null);
  const [child, setChild] = useState(null);
  const [classInfo, setClassInfo] = useState(null);
  const [showDownloadLink, setShowDownloadLink] = useState(false);
  const [bestFriend, setBestFriend] = useState(null);
  const [currentStudent, setCurrentStudent] = useState(null);

  const { currentProject } = useCurrentProject();

  const open = Boolean(anchorEl);

  const handleClick = (event, child) => {
    setBestFriend(null);
    setChild({ ...child });
    setAnchorEl(event.currentTarget);
  };

  const handleClickClass = (event, classObj) => {
    setBestFriend(null);
    setClassInfo({ ...classObj });
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setBestFriend(null);
    setClassInfo(null);
    setChild(null);
    setAnchorEl(null);
  };

  const handleChange = ({ target }: { target: HTMLInputElement }) => {
    setBestFriend(null);
    const { name, value, checked } = target;

    setStudentsFilter({
      ...studentsFilter,
      [name]: Boolean(checked),
    });
  };

  useEffect(() => {
    setBestFriend(null);
    fetchDistribution();
  }, [studentsFilter]);

  const onManualModeChange = async (e: any) => {
    setBestFriend(null);
    if (!e.target.checked) return;

    await enableManualMode();
  };

  const boardData = useMemo(() => {
    if (!distributionMap) {
      return { lanes: [] };
    }

    return {
      lanes: distributionMap
        .sort((a, b) =>
          a.institution.address.localeCompare(b.institution.address)
        )
        .map((classItem, index) => ({
          id: "class" + index,
          title: classItem.name,
          teacherName: classItem.teacherName,
          label: "Km",
          maxStudentsNumber: classItem?.maxStudentsNumber,
          minStudentsNumber: classItem?.minStudentsNumber,
          schoolBus: classItem?.schoolBus,
          directAssignmentCount: classItem?.stats?.directAssignmentCount,
          femalesCount: classItem?.stats?.femalesCount,
          lowGermanLevelCount: classItem?.stats?.lowGermanLevelCount,
          knowledgeBasicCount: classItem?.stats?.knowledgeBasicCount,
          insCount: classItem?.stats?.insCount,
          malesCount: classItem?.stats?.malesCount,
          germanAsSecondLanguageCount:
            classItem?.stats?.germanAsSecondLanguageCount,
          logopedicCount: classItem?.stats?.logopedicCount,
          germanCount: classItem?.stats?.germanCount,
          mathsCount: classItem?.stats?.mathsCount,
          lzaGermanCount: classItem?.stats?.lzaGermanCount,
          lzaMathsCount: classItem?.stats?.lzaMathsCount,
          psychomotorCount: classItem?.stats?.psychomotorCount,
          collapsed: classItem?.stats?.malesCount,
          onClick: classItem?.stats?.femalesCount,
          cards: classItem.students
            .sort((a, b) => {
              if (a.lastName && b.lastName) {
                return a.lastName.localeCompare(b.lastName);
              } else {
                return a.fullName.localeCompare(b.fullName);
              }
            })
            .map((kid) => ({
              id: kid._id,
              susId: kid?.susId,
              title: `${kid.lastName} ${kid.firstName}`,
              label: kid.distanceToClass ? kid.distanceToClass.toFixed(2) : "",
              icon: generateIcon(kid, classItem),
              gender: kid?.gender,
              address: kid?.address,
              germanLanguageLevel: kid?.germanLanguageLevel,
              previousExperience: kid?.previousExperience,
              schoolBus: kid?.schoolBus,
              teacherName: kid?.teacherName,
              birthday: kid?.birthday,
              bestFriendName: kid?.bestFriendName,
              knowledgeLevel: kid?.knowledgeLevel,
              specialNeeds: kid?.specialNeeds,
              helpWithStudy: kid?.helpWithStudy,
              note: kid?.note,
              teacher: kid?.teacher,
              knowledgeLevelNote: kid?.knowledgeLevelNote,
              knowledgeLevelSpecialNote: kid?.knowledgeLevelSpecialNote,
              inS: kid?.inS,
              specialNeedsNote: kid?.specialNeedsNote,
              specialNote: kid?.specialNote,
            }))
            .sort((a, b) => {
              if (a.title < b.title) {
                return -1;
              }
              if (a.title > b.title) {
                return 1;
              }
              return 0;
            }),
        })),
    };
  }, [distributionMap]);

  const handleMoveCard = async (cardId, _sourceLaneId, targetLaneId) => {
    setBestFriend(null);
    const targetColumn = boardData?.lanes.filter(
      (e) => e.id === targetLaneId
    )[0];

    setIsLoading(true);

    try {
      await studentsApi.edit({
        teacherName: targetColumn?.teacherName,
        id: cardId,
      });
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }

    await fetchDistribution();
  };

  const showBestFriend = (bestFriendName: string, fullName) => {
    for (let i = 0; i < distributionMap.length; i++) {
      const group = distributionMap[i];
      const studentIndex = group.students.findIndex(student => student.fullName === bestFriendName);
      if (studentIndex !== -1) {
        setBestFriend({
          bestFriendName: bestFriendName,
          className: group.name,
          fullName: fullName
        })
      }
    }
    return null;
  };

  const borderStyle = (currentStudentName: string, bestFriend: any, isBottom = false) => {
    if (bestFriend && bestFriend.bestFriendName === currentStudentName) {
      return '3px solid #4F77DE'
    }
    if (bestFriend && bestFriend.fullName === currentStudentName) {
      return '3px solid #9db5f5'
    }
    if (isBottom) return '0.6px solid #D5D5D5'
    return '3px solid transparent'
  }

  const opacityStyle = (currentStudentName: string, bestFriend: any) => {
    if (bestFriend && currentStudentName === bestFriend?.fullName) return '1'
    if (bestFriend && currentStudentName !== bestFriend?.bestFriendName) return '0.65'
    return '1'
  }

  const CustomCard = (props: any) => {
    const {
      title,
      label,
      icon,
      gender,
      address,
      germanLanguageLevel,
      previousExperience,
      schoolBus,
      teacherName,
      birthday,
      bestFriendName,
      knowledgeLevel,
      specialNeeds,
      helpWithStudy,
      note,
      teacher,
      susId,
      knowledgeLevelNote,
      knowledgeLevelSpecialNote,
      inS,
      specialNeedsNote,
      specialNote,
    } = props;
    const currentTarget = {
      fullName: title,
      gender: gender,
      address: address,
      germanLanguageLevel: germanLanguageLevel,
      distanceToClass: label,
      previousExperience,
      schoolBus,
      teacherName,
      birthday,
      bestFriendName,
      knowledgeLevel,
      specialNeeds,
      helpWithStudy,
      teacher,
      susId,
      knowledgeLevelNote,
      knowledgeLevelSpecialNote,
      inS,
      specialNeedsNote,
      specialNote,
      note,
    };
    return (
      <S.Card
        style={{
          border: borderStyle(title, bestFriend),
          borderBottom: borderStyle(title, bestFriend, true),
          opacity: opacityStyle(title, bestFriend)
        }}
        className={`card ${gender === "male" ? "blue" : "rose"}`}
      >
        <h3>
          <p onClick={(e) => handleClick(e, currentTarget)}>{title}</p>
          {icon?.lgIcon || icon?.daIcon &&
            <span style={{ fontSize: 12, fontWeight: 400 }}>
              {icon?.lgIcon && <img src={icon.lgIcon} />}
              {icon?.daIcon && <img src={icon.daIcon} />}
            </span>
          }
          {bestFriend && title === bestFriend?.bestFriendName && <span className="clear" onClick={() => setBestFriend(null)}>x</span>}
          {bestFriendName &&
            <span className="friend" onClick={(e) => showBestFriend(bestFriendName, title)}>
              <img src={friends} alt={bestFriendName} />
            </span>}
          <p>{label} </p>
        </h3>
      </S.Card>
    );
  };

  const CustomHeader = (props: any) => {
    const {
      title,
      label,
      knowledgeBasicCount,
      insCount,
      germanAsSecondLanguageCount,
      logopedicCount,
      germanCount,
      mathsCount,
      lzaGermanCount,
      lzaMathsCount,
      psychomotorCount,
      teacherName,
      maxStudentsNumber,
      minStudentsNumber,
      schoolBus,
      directAssignmentCount,
      femalesCount,
      lowGermanLevelCount,
      malesCount,
    } = props;
    const currentTarget = {
      name: title,
      teacherName,
      maxStudentsNumber,
      minStudentsNumber,
      schoolBus,
      directAssignmentCount,
      femalesCount,
      knowledgeBasicCount,
      insCount,
      germanAsSecondLanguageCount,
      logopedicCount,
      germanCount,
      mathsCount,
      lzaGermanCount,
      lzaMathsCount,
      psychomotorCount,
      lowGermanLevelCount,
      malesCount,
    };
    return (
      <>
        <header
          style={{
            border: bestFriend && bestFriend.className === title && '2px solid #4F77DE'
          }}
          onClick={(e) => handleClickClass(e, currentTarget)}>
          <span>{title}</span>
          <span>{label}</span>
          <strong
            style={{
              position: "absolute",
              bottom: -30,
              width: "100%",
              left: 0,
              padding: "0 15px",
            }}
          >
            Total: {femalesCount + malesCount}
          </strong>
        </header>
      </>
    );
  };

  const components = {
    Card: (props: any) => CustomCard({ ...props }),
    LaneHeader: (props: any) => CustomHeader({ ...props }),
  };

  const csvData = useMemo(() => {
    if (!distributionMap) return [];

    const rows: any[] = [];
    const headers = [
      "Name",
      "Vorname",
      "Adresse",
      "Geschlecht",
      "Letzte Lehrperson",
      "Lehrperson",
      "Deutsch",
      "Freunde",
      "Anforderungen",
      "Anforderungen wurde abgeklärt (SPL, Arzt)",
      "Anforderungen Grund",
      "Fördermassnahmen",
      "Fördermassnahmen Weitere",
      "Lernbegleitung SHP",
      "Bemerkungen",
      "Stärken / Ressource",
      "Distanz"
    ];

    distributionMap.forEach((classItem) => {
      classItem.students.forEach((student) => {
        rows.push([
          student.lastName,
          student.firstName,
          student.address,
          getGender(student.gender),
          student.teacher,
          classItem.teacherName,
          getGermanLanguageLevelForStudent(student.germanLanguageLevel),
          student.bestFriendName,
          getKnowledgeLevel(student.knowledgeLevel),
          student.knowledgeLevelNote,
          student.knowledgeLevelSpecialNote,
          student.specialNeeds.map((need) => getSpecialNeeds(need)).join("' "),
          student.specialNeedsNote,
          student.helpWithStudy
            .map((helpWithStudy) => getHelpWithStudy(helpWithStudy))
            .join("' "),

          student.note,
          student.specialNote,
          student.distanceToClass ? student.distanceToClass.toFixed(2) : "",
        ]);
      });
    });

    return [headers, ...rows];
  }, [distributionMap]);

  return (
    <commonS.MainBody>
      <MainContentHeader
        title={currentProject?.title || ""}
        buttonTitle="Daten-Export"
        isButtonDisabled={Boolean(!distributionMap)}
        onButtonClick={() => {
          setShowDownloadLink(true);
          setTimeout(() => setShowDownloadLink(false), 60000);
        }}
      />

      {showDownloadLink && (
        <CSVLink
          style={{ marginBottom: 20 }}
          onClick={() => setShowDownloadLink(false)}
          data={csvData}
        >
          Laden Sie hier die CSV-Datei herunter
        </CSVLink>
      )}

      <S.FilterWrap>
        <S.FilterContent className="content">
          <S.FilterItem>
            <FormControlLabel
              control={
                <Checkbox
                  name="gender"
                  onChange={handleChange}
                  checked={studentsFilter.gender}
                />
              }
              label="Geschlecht"
            />
          </S.FilterItem>
          <S.FilterItem>
            <FormControlLabel
              control={
                <Checkbox
                  name="knowledgeLevel"
                  onChange={handleChange}
                  checked={studentsFilter.knowledgeLevel}
                />
              }
              label="Anforderungen"
            />
          </S.FilterItem>
        </S.FilterContent>

        <FormControlLabel
          sx={{ marginLeft: "auto" }}
          control={
            <Switch
              checked={isManualMode}
              disabled={isManualMode}
              onChange={onManualModeChange}
            />
          }
          label="Manuelle Einteilung"
        />
      </S.FilterWrap>

      <S.Wrap>
        <Board
          data={boardData}
          handleDragEnd={handleMoveCard}
          components={components}
        />
      </S.Wrap>
      {open && child && (
        <StudentHint
          student={child}
          paperProps={{
            sx: {
              top: open
                ? `280px !important`
                : 0,
              left: open
                ? `${anchorEl?.getBoundingClientRect().left}px !important`
                : 0,
              transformOrigin: `0 -${anchorEl?.getBoundingClientRect().left}px !important`,
              transition: "opacity 0s ease !important",
            },
          }}
          handleClose={handleClose}
          anchorEl={anchorEl}
        />
      )}
      {open && classInfo && (
        <SchoolClassHint
          classInfo={classInfo}
          paperProps={{
            sx: {
              top: open
                ? `280px !important`
                : 0,
              left: open
                ? `${anchorEl?.getBoundingClientRect().left}px !important`
                : 0,
              transformOrigin: `0 -${anchorEl?.getBoundingClientRect().left}px !important`,
              transition: "opacity 0s ease !important",
            },
          }}
          handleClose={handleClose}
          anchorEl={anchorEl}
        />
      )}
      {isLoading && <Loader filled={true} />}
    </commonS.MainBody>
  );
};

export default observer(StudentsDistribution);
